import React from 'react';

interface QueueHealthCardProps {
  metrics: {
    pendingPayouts: number;
    autoEligible: number;
    manualRequired: number;
    pendingSignatures: number;
    oldestPendingMinutes: number;
    averagePendingMinutes: number;
  } | null;
}

const QueueHealthCard: React.FC<QueueHealthCardProps> = ({ metrics }) => {
  if (!metrics) {
    return (
      <div className="card p-6 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
        <div className="h-8 bg-gray-200 rounded w-1/2"></div>
      </div>
    );
  }

  const pendingCount = metrics.pendingPayouts;
  const status: 'healthy' | 'warning' | 'critical' =
    pendingCount >= 20 ? 'critical' : pendingCount >= 10 ? 'warning' : 'healthy';

  const statusColors = {
    healthy: 'border-green-500 bg-green-50',
    warning: 'border-yellow-500 bg-yellow-50',
    critical: 'border-red-500 bg-red-50',
  };

  const badgeColors = {
    healthy: 'bg-green-100 text-green-800',
    warning: 'bg-yellow-100 text-yellow-800',
    critical: 'bg-red-100 text-red-800',
  };

  const formatMinutes = (minutes: number): string => {
    if (minutes === 0) return 'None';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  return (
    <div className={`card border-l-4 ${statusColors[status]} p-6`}>
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <h3 className="text-sm font-medium text-gray-500 mb-1">Payout Queue</h3>
          <p className="text-3xl font-bold text-gray-900">{pendingCount}</p>
          <p className="text-sm text-gray-600 mt-1">pending requests</p>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badgeColors[status]}`}>
          {status.toUpperCase()}
        </span>
      </div>

      {/* Queue Breakdown */}
      <div className="space-y-2 mb-4">
        <div className="flex justify-between items-center text-sm">
          <span className="text-gray-600">Auto-eligible:</span>
          <span className="font-medium text-gray-900">{metrics.autoEligible}</span>
        </div>
        <div className="flex justify-between items-center text-sm">
          <span className="text-gray-600">Manual required:</span>
          <span className="font-medium text-gray-900">{metrics.manualRequired}</span>
        </div>
        <div className="flex justify-between items-center text-sm">
          <span className="text-gray-600">Pending signatures:</span>
          <span className="font-medium text-orange-600">{metrics.pendingSignatures}</span>
        </div>
      </div>

      {/* Wait Time Stats */}
      <div className="pt-4 border-t border-gray-200">
        <div className="grid grid-cols-2 gap-4 text-xs">
          <div>
            <p className="text-gray-500">Oldest Request</p>
            <p className="font-semibold text-gray-900 mt-1">
              {formatMinutes(metrics.oldestPendingMinutes)}
            </p>
          </div>
          <div>
            <p className="text-gray-500">Average Wait</p>
            <p className="font-semibold text-gray-900 mt-1">
              {formatMinutes(Math.floor(metrics.averagePendingMinutes))}
            </p>
          </div>
        </div>
      </div>

      {/* Status Messages */}
      {status === 'critical' && (
        <div className="mt-3 p-2 bg-red-100 border border-red-200 rounded text-xs text-red-800">
          <strong>Critical:</strong> Queue depth exceeds {pendingCount} requests. Immediate action required.
        </div>
      )}
      {status === 'warning' && (
        <div className="mt-3 p-2 bg-yellow-100 border border-yellow-200 rounded text-xs text-yellow-800">
          <strong>Warning:</strong> Queue depth is elevated. Monitor for backlog.
        </div>
      )}
    </div>
  );
};

export default QueueHealthCard;
